// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import "./AdminHome.css";
import "./CollectionDetail.css";
import { useNavPinned } from "./useNavPinned";
import AdminTopNav from "./AdminTopNav";
import { API_ENDPOINTS } from "../config/api";
import { getFieldLabel, getCollectionSchema, getFieldOrder } from "./collectionSchemas";
import ConfirmDialog from "./ConfirmDialog";


export default function CollectionDetail() {
    const { collectionName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [navPinned, setNavPinned] = useNavPinned();
    const [showColSettings, setShowColSettings] = useState(false);
    const [colOverrides, setColOverrides] = useState({});
    const colSettingsRef = React.useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [columnWidths, setColumnWidths] = useState({});
    const [resizingCol, setResizingCol] = useState(null);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);
    const [resendingId, setResendingId] = useState(null); // row id currently being resent
    const [resendErrors, setResendErrors] = useState({}); // rowId -> error message
    const [postNumSort, setPostNumSort] = useState('desc');

    const hasPostNumSort = collectionName === 'BlogContent' || collectionName === 'PodcastsAudioDetails';
    const displayData = React.useMemo(() => {
        if (!hasPostNumSort) return data;
        return [...data].sort((a, b) => {
            const an = typeof a.postNumber === 'number' ? a.postNumber : Number(a.postNumber) || 0;
            const bn = typeof b.postNumber === 'number' ? b.postNumber : Number(b.postNumber) || 0;
            return postNumSort === 'desc' ? bn - an : an - bn;
        });
    }, [data, postNumSort, hasPostNumSort]);

    // Get user role from localStorage
    const getUserRole = () => {
        try {
            const userStr = sessionStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                return user.role || null;
            }
        } catch (e) {
            console.error("Error parsing user data:", e);
        }
        return null;
    };
    
    const userRole = getUserRole();
    const isAdmin = userRole === 'admin';
    const isStaff = userRole === 'staff'; // eslint-disable-line no-unused-vars
    
    // Detect database from URL path
    let databaseType = 'creddb';
    let databaseName = 'CredDb';
    if (location.pathname.includes('/blogdb/')) {
        databaseType = 'blogdb';
        databaseName = 'BlogDb';
    } else if (location.pathname.includes('/newsletterdb/')) {
        databaseType = 'newsletterdb';
        databaseName = 'NewsLetterDb';
    } else if (location.pathname.includes('/podcastdb/')) {
        databaseType = 'podcastdb';
        databaseName = 'PodcastDb';
    } else if (location.pathname.includes('/storedb/')) {
        databaseType = 'storedb';
        databaseName = 'StoreDb';
    } else if (location.pathname.includes('/sponsorshipdb/')) {
        databaseType = 'sponsorshipdb';
        databaseName = 'SponsorshipDb';
    } else if (location.pathname.includes('/userthoughts/')) {
        databaseType = 'userthoughts';
        databaseName = 'UserThoughts';
    } else if (location.pathname.includes('/newsroomdb/')) {
        databaseType = 'newsroomdb';
        databaseName = 'NewsRoomDb';
    } else if (location.pathname.includes('/learnwithai/')) {
        databaseType = 'learnwithai';
        databaseName = 'LearnWithAI';
    } else if (location.pathname.includes('/socialmediadb/')) {
        databaseType = 'socialmediadb';
        databaseName = 'SocialMediaDb';
    } else if (location.pathname.includes('/affiliateproductsdb/')) {
        databaseType = 'affiliateproductsdb';
        databaseName = 'AffiliateProductsDb';
    }

    // Builds the correct API URL for document-level operations (GET/PUT/DELETE)
    // based on which DB the collection belongs to.
    const getDocUrl = (collection, docId) => {
        if (databaseType === 'newsroomdb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_NEWSROOMDB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'learnwithai') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_LEARNWITHAI}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'socialmediadb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_SOCIALMEDIADB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        if (databaseType === 'affiliateproductsdb') {
            const base = `${API_ENDPOINTS.ADMIN.DATABASE_AFFILIATEPRODUCTSDB}/${encodeURIComponent(collection)}`;
            return docId ? `${base}/${encodeURIComponent(docId)}` : base;
        }
        return docId
            ? API_ENDPOINTS.ADMIN.COLLECTION_DELETE(collection, docId)
            : API_ENDPOINTS.ADMIN.COLLECTION_CREATE(collection);
    };

    const getAuthHeaders = () => {
        const token = sessionStorage.getItem("token");
        return {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
    };

    const fetchCollectionData = async () => {
        setLoading(true);
        setError("");
        try {
            // Determine the fetch URL based on database type
            let fetchUrl = API_ENDPOINTS.ADMIN.DATABASE_COLLECTION(collectionName);
            if (databaseType === 'affiliateproductsdb') {
                fetchUrl = `${API_ENDPOINTS.ADMIN.DATABASE_AFFILIATEPRODUCTSDB}/${encodeURIComponent(collectionName)}`;
            } else if (databaseType === 'socialmediadb') {
                fetchUrl = `${API_ENDPOINTS.ADMIN.DATABASE_SOCIALMEDIADB}/${encodeURIComponent(collectionName)}`;
            } else if (databaseType === 'newsroomdb') {
                fetchUrl = `${API_ENDPOINTS.ADMIN.DATABASE_NEWSROOMDB}/${encodeURIComponent(collectionName)}`;
            } else if (databaseType === 'learnwithai') {
                fetchUrl = `${API_ENDPOINTS.ADMIN.DATABASE_LEARNWITHAI}/${encodeURIComponent(collectionName)}`;
            }

            const res = await fetch(fetchUrl, {
                headers: getAuthHeaders(),
                credentials: "include",
                cache: "no-store"
            });
            
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Failed to load collection (${res.status})`);
            }
            
            const json = await res.json();
            setData(Array.isArray(json.data) ? json.data : []);
            setCount(typeof json.count === 'number' ? json.count : json.data.length);
        } catch (e) {
            setError(e.message || "Failed to load collection data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (collectionName) {
            fetchCollectionData();
        }
    }, [collectionName]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResend = async (rowId) => {
        setResendingId(rowId);
        setResendErrors(prev => ({ ...prev, [rowId]: null }));
        try {
            const res = await fetch(API_ENDPOINTS.ADMIN.SEND_NOTIFICATION(collectionName, rowId), {
                method: 'POST',
                headers: getAuthHeaders(),
                credentials: 'include'
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.message || 'Failed to send notification');
            }
            // Update the row in local state with the new emailNotification
            setData(prev => prev.map(row => {
                const id = String(row._id || row.id || '');
                return id === rowId ? { ...row, emailNotification: json.emailNotification } : row;
            }));
        } catch (e) {
            setResendErrors(prev => ({ ...prev, [rowId]: e.message }));
        } finally {
            setResendingId(null);
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRows(newSelected);
    };

    const handleDeleteSelected = async () => {
        if (selectedRows.size === 0) {
            return;
        }
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        setShowDeleteDialog(false);
        
        try {
            const token = sessionStorage.getItem("token"); // eslint-disable-line no-unused-vars
            const selectedData = Array.from(selectedRows).map(id => data.find(d => String(d._id || d.id) === id)).filter(Boolean);
            const idsToDelete = selectedData.map(item => String(item._id || item.id)).filter(Boolean);

            if (idsToDelete.length === 0) {
                alert("No valid IDs found for deletion");
                return;
            }

            console.log('Deleting documents:', { collectionName, idsToDelete });

            // Delete via API endpoint
            const deletePromises = idsToDelete.map(async (id) => {
                try {
                    const url = getDocUrl(collectionName, id);
                    console.log('Delete URL:', url);
                    
                    const res = await fetch(url, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                        credentials: "include"
                    });

                    const contentType = res.headers.get("content-type");
                    
                    if (!res.ok) {
                        let errorMessage = `Failed to delete document (${res.status})`;
                        
                        if (contentType && contentType.includes("application/json")) {
                            try {
                                const errorJson = await res.json();
                                errorMessage = errorJson.message || errorMessage;
                            } catch {
                                // If JSON parsing fails, use default message
                            }
                        } else {
                            const text = await res.text();
                            if (text.length < 200) {
                                errorMessage = text;
                            }
                        }
                        
                        return { success: false, id, error: errorMessage };
                    }

                    const json = await res.json();
                    return { success: true, id, data: json };
                } catch (err) {
                    console.error(`Error deleting document ${id}:`, err);
                    return { success: false, id, error: err.message };
                }
            });

            const results = await Promise.allSettled(deletePromises);
            const failed = [];
            const succeeded = [];

            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    const errorMessage = (result.reason && result.reason.message) || 'Unknown error';
                    failed.push({ id: idsToDelete[index], error: errorMessage });
                } else {
                    const value = result.value;
                    if (value.success) {
                        succeeded.push(value.id);
                    } else {
                        failed.push({ id: value.id, error: value.error });
                    }
                }
            });

            if (failed.length > 0) {
                const errorMessages = failed.map(f => `ID ${f.id}: ${f.error}`).join('\n');
                alert(`Failed to delete ${failed.length} document(s):\n${errorMessages}`);
            }
            
            if (succeeded.length > 0) {
                alert(`Successfully deleted ${succeeded.length} document(s)`);
                setSelectedRows(new Set());
                fetchCollectionData();
            }
        } catch (e) {
            console.error('Error deleting documents:', e);
            alert(`Error deleting documents: ${e.message}`);
        }
    };


    const handleCreateNew = () => {
        navigate(`/admin/database/${databaseType}/${collectionName}/create`);
    };

    // Get all unique keys from data for table headers
    const getAllKeys = () => {
        const keysSet = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== '__v') {
                    keysSet.add(key);
                }
            });
        });
        
        // If we have a schema with fieldOrder, use it
        const fieldOrder = getFieldOrder(collectionName);
        if (fieldOrder) {
            const orderedKeys = [];
            // Add _id first if it exists
            if (keysSet.has('_id')) {
                orderedKeys.push('_id');
                keysSet.delete('_id');
            }
            // Add fields in the specified order
            fieldOrder.forEach(field => {
                if (keysSet.has(field)) {
                    orderedKeys.push(field);
                    keysSet.delete(field);
                }
            });
            // Add remaining fields that aren't in the order
            Array.from(keysSet).sort().forEach(key => orderedKeys.push(key));
            return orderedKeys;
        }
        
        // If we have a schema without fieldOrder, use required fields first
        const schema = getCollectionSchema(collectionName);
        if (schema) {
            const orderedKeys = [];
            // Add _id first if it exists
            if (keysSet.has('_id')) {
                orderedKeys.push('_id');
                keysSet.delete('_id');
            }
            // Add required fields in order
            schema.requiredFields.forEach(field => {
                if (keysSet.has(field)) {
                    orderedKeys.push(field);
                    keysSet.delete(field);
                }
            });
            // Add other fields from schema
            Object.keys(schema.fieldTypes).forEach(field => {
                if (keysSet.has(field)) {
                    orderedKeys.push(field);
                    keysSet.delete(field);
                }
            });
            // Add remaining fields
            Array.from(keysSet).sort().forEach(key => orderedKeys.push(key));
            return orderedKeys;
        }
        
        // Default sorting if no schema
        return Array.from(keysSet).sort((a, b) => {
            // Put _id first if it exists
            if (a === '_id') return -1;
            if (b === '_id') return 1;
            return a.localeCompare(b);
        });
    };

    const getDefaultVisibleKeys = (rawKeys) => {
        let defaultVisible = rawKeys.filter(key => key !== 'createdAt');
        if (collectionName === 'Users') {
            const allowed = ['name', 'email', 'role', 'isSubscribed'];
            return rawKeys.filter(k => allowed.includes(k));
        }
        if (collectionName === 'AuthorDetails') {
            const allowed = ['name', 'authorNumber', 'bio'];
            return rawKeys.filter(k => allowed.includes(k));
        }
        if (collectionName === 'BlogContent' || collectionName === 'blogcontent' || collectionName === 'blogcontents') {
            const preferred = ['postNumber', 'title', 'author', 'emailNotification'];
            return preferred.filter(k => rawKeys.includes(k) || k === 'emailNotification');
        }
        if (collectionName === 'Category' || collectionName === 'category' || collectionName === 'categories') {
            const allowed = ['name', 'slug', 'seoTitle'];
            return rawKeys.filter(k => allowed.includes(k));
        }
        if (collectionName === 'ImageAssets' || collectionName === 'imageassets') {
            return defaultVisible.filter(key => key !== '_id' && key !== 'id');
        }
        if (collectionName === 'PostTopics' || collectionName === 'posttopics' || collectionName === 'posttopic') {
            return defaultVisible.filter(key => key !== '_id' && key !== 'id');
        }
        if (collectionName === 'PodcastGuests' || collectionName === 'podcastguests' || collectionName === 'podcastguest') {
            return defaultVisible.filter(key => key !== '_id' && key !== 'id' && key !== 'image' && key !== 'socialLinks');
        }
        if (collectionName === 'PodcastsAudioDetails' || collectionName === 'podcastsaudiodetails') {
            const preferred = ['podcastNumber', 'title', 'slug', 'date', 'hosts', 'emailNotification'];
            return preferred.filter(k => rawKeys.includes(k) || k === 'emailNotification');
        }
        if (collectionName === 'CouponCode' || collectionName === 'couponcode') {
            const allowed = ['name', 'code', 'product', 'offerStartDate', 'offerEndDate', 'offerIsVisible'];
            return rawKeys.filter(k => allowed.includes(k));
        }
        if (collectionName === 'ProductDetails' || collectionName === 'productdetails') {
            const allowed = ['title', 'slug', 'price', 'offerPrice', 'stockAvailability', 'canPurchase', 'isVisible'];
            return rawKeys.filter(k => allowed.includes(k));
        }
        if (collectionName === 'PurchaseOrders' || collectionName === 'purchaseorders') {
            let pKeys = defaultVisible.filter(key => key !== '_id' && key !== 'id');
            const updatedAtIndex = pKeys.indexOf('updatedAt');
            if (updatedAtIndex > -1) {
                pKeys.splice(updatedAtIndex, 1);
                pKeys.unshift('updatedAt');
            }
            return pKeys;
        }
        if (collectionName === 'AdPromotions' || collectionName === 'adpromotions') {
            return defaultVisible.filter(key => key !== '_id' && key !== 'id' && key !== 'image');
        }
        return defaultVisible;
    };

    const toggleColumnVisibility = (key) => {
        const rawKeys = getAllKeys().filter(k => k !== '__v');
        const defaults = getDefaultVisibleKeys(rawKeys);
        const currentlyVisible = colOverrides[key] !== undefined ? colOverrides[key] : defaults.includes(key);
        setColOverrides(prev => {
            const next = { ...prev, [key]: !currentlyVisible };
            localStorage.setItem(`admin_col_vis_${collectionName}`, JSON.stringify(next));
            return next;
        });
    };

    const renderCellValue = (key, value, rowId = '') => {
        if (key === 'emailNotification') {
            const isSending = resendingId === rowId;
            const err = resendErrors[rowId];
            // Normalise: support old single-object format and new array format; show most recent (index 0)
            const latest = Array.isArray(value) ? value[0] : (value && value.sentAt ? value : null);
            const hasSent = latest && latest.templateName && latest.sentAt;
            return (
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {hasSent ? (
                        <>
                            <span style={{ fontSize: '11px', fontWeight: 500 }}>{latest.templateName}</span>
                            <span style={{ fontSize: '10px', color: '#888' }}>
                                {new Date(latest.sentAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                                {latest.successCount != null && ` · ${latest.successCount} subscribers`}
                            </span>
                        </>
                    ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '11px' }}>Not sent</span>
                    )}
                    {isAdmin && rowId && (
                        <button
                            onClick={() => handleResend(rowId)}
                            disabled={isSending}
                            style={{
                                marginTop: '3px',
                                padding: '2px 8px',
                                fontSize: '10px',
                                background: isSending ? '#d1d5db' : '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: isSending ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                alignSelf: 'flex-start'
                            }}
                        >
                            {isSending ? 'Sending…' : hasSent ? 'Resend' : 'Send'}
                        </button>
                    )}
                    {err && <span style={{ fontSize: '10px', color: '#dc2626' }}>{err}</span>}
                </span>
            );
        }
        if (value === null || value === undefined || value === '') {
            return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
        }
        if (typeof value === 'boolean') {
            return <span style={{ color: value ? '#059669' : '#6b7280' }}>{value ? '✓' : '✗'}</span>;
        }
        if (typeof value === 'number') {
            return <span style={{ fontSize: '12px' }}>{value}</span>;
        }
        if (typeof value === 'object') {
            // Handle arrays
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>[]</span>;
                }
                
                // Special handling for PodcastsAudioDetails hosts array
                if (collectionName === 'PodcastsAudioDetails' && key === 'hosts') {
                    if (value.length === 0) {
                        return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No hosts</span>;
                    }
                    // Display host names if available, otherwise show count
                    const hostNames = value
                        .map(host => {
                            if (typeof host === 'object' && host !== null) {
                                // Prioritize hostName (from populated data) over name
                                return host.hostName || host.name || (host._id ? String(host._id).substring(0, 8) + '...' : 'Unknown');
                            }
                            // If it's a string ID, show truncated ID
                            if (typeof host === 'string' && host.length === 24) {
                                return host.substring(0, 8) + '...';
                            }
                            return String(host);
                        })
                        .filter(Boolean);
                    
                    if (hostNames.length > 0) {
                        return (
                            <span style={{ fontSize: '12px' }} title={hostNames.join(', ')}>
                                {hostNames.length} Host{hostNames.length !== 1 ? 's' : ''}: {hostNames.slice(0, 2).join(', ')}{hostNames.length > 2 ? ` +${hostNames.length - 2} more` : ''}
                            </span>
                        );
                    }
                    return <span style={{ fontSize: '12px' }}>{value.length} Host{value.length !== 1 ? 's' : ''}</span>;
                }
                
                // Special handling for PurchaseOrders products array
                if (collectionName === 'PurchaseOrders' && key === 'products') {
                    return (
                        <div style={{ maxWidth: '400px' }}>
                            <div style={{ 
                                fontSize: '11px', 
                                fontWeight: 600, 
                                color: '#374151', 
                                marginBottom: '6px' 
                            }}>
                                {value.length} Product{value.length !== 1 ? 's' : ''}
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '6px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                padding: '8px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                            }}>
                                {value.map((product, idx) => (
                                    <div 
                                        key={idx}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: 'white',
                                            borderRadius: '4px',
                                            border: '1px solid #e5e7eb',
                                            fontSize: '11px'
                                        }}
                                    >
                                        <div style={{ fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                            Product {idx + 1}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', color: '#6b7280' }}>
                                            {product.productId && (
                                                <div>ID: <span style={{ fontFamily: 'monospace' }}>{String(product.productId).substring(0, 20)}</span></div>
                                            )}
                                            {product.quantity !== undefined && (
                                                <div>Qty: {product.quantity}</div>
                                            )}
                                            {product.price !== undefined && (
                                                <div>Price: ₹{product.price.toLocaleString()}</div>
                                            )}
                                            {product.subtotal !== undefined && (
                                                <div style={{ fontWeight: 600, color: '#059669' }}>
                                                    Subtotal: ₹{product.subtotal.toLocaleString()}
                                                </div>
                                            )}
                                            {product.title && (
                                                <div style={{ marginTop: '4px', fontStyle: 'italic' }}>
                                                    {product.title}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
                
                // Special handling for PurchaseOrders payment array (if it's an array)
                if (collectionName === 'PurchaseOrders' && key === 'payment' && Array.isArray(value)) {
                    return (
                        <div style={{ maxWidth: '300px' }}>
                            <div style={{ 
                                fontSize: '11px', 
                                fontWeight: 600, 
                                color: '#374151', 
                                marginBottom: '6px' 
                            }}>
                                Payment Details
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '6px',
                                padding: '8px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                            }}>
                                {value.map((payment, idx) => (
                                    <div 
                                        key={idx}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: 'white',
                                            borderRadius: '4px',
                                            border: '1px solid #e5e7eb',
                                            fontSize: '11px'
                                        }}
                                    >
                                        {payment.method && <div><strong>Method:</strong> {payment.method}</div>}
                                        {payment.status && <div><strong>Status:</strong> {payment.status}</div>}
                                        {payment.transactionId && (
                                            <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#6b7280' }}>
                                                Txn ID: {payment.transactionId}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
                
                // Special handling for CouponCode product array
                if (collectionName === 'CouponCode' && key === 'product') {
                    const productNames = value.map(p => {
                        if (typeof p === 'object' && p !== null) {
                            return p.title || p.name || (p._id ? String(p._id).substring(0, 8) + '...' : 'Unknown');
                        }
                        return String(p).substring(0, 8) + '...';
                    });
                    
                    if (productNames.length === 0) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>All Products</span>;
                    
                    return (
                        <span style={{ fontSize: '12px' }} title={productNames.join(', ')}>
                            {productNames.slice(0, 3).join(', ')}{productNames.length > 3 ? ` +${productNames.length - 3} more` : ''}
                        </span>
                    );
                }
                
                // Special handling for author array
                if (key === 'author') {
                    const authorNames = value.map(a => {
                        if (typeof a === 'object' && a !== null) {
                            return a.name || a.authorName || (a._id ? String(a._id).substring(0, 8) + '...' : 'Unknown');
                        }
                        return String(a).substring(0, 8) + '...';
                    });
                    
                    if (authorNames.length === 0) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No Authors</span>;
                    
                    return (
                        <span style={{ fontSize: '12px' }} title={authorNames.join(', ')}>
                            {authorNames.slice(0, 3).join(', ')}{authorNames.length > 3 ? ` +${authorNames.length - 3} more` : ''}
                        </span>
                    );
                }
                
                // Show array summary with first few items
                const preview = value.slice(0, 3).map((item, idx) => {
                    if (typeof item === 'object' && item !== null) {
                        return `{${Object.keys(item).slice(0, 2).join(', ')}}`;
                    }
                    return String(item).substring(0, 20);
                }).join(', ');
                const more = value.length > 3 ? ` +${value.length - 3} more` : '';
                return (
                    <span style={{ fontSize: '12px' }} title={JSON.stringify(value, null, 2)}>
                        [{preview}{more}]
                    </span>
                );
            }
            // Handle objects
            if (Object.keys(value).length === 0) {
                return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{}</span>;
            }
            // For specific object types, show a summary
            if (key === 'coverImage' && value.url) {
                return <span style={{ fontSize: '12px' }} title={JSON.stringify(value, null, 2)}>🖼️ {value.fileName || value.url.substring(0, 30)}</span>;
            }
            if (key === 'author' && (value.name || value._id)) {
                return <span style={{ fontSize: '12px' }}>{value.name || value._id || 'N/A'}</span>;
            }
            if ((key === 'category' || key === 'postTopic') && (value.name || value.topic || value._id)) {
                return <span style={{ fontSize: '12px' }}>{value.name || value.topic || value._id || 'N/A'}</span>;
            }
            // Special handling for BlogComments blogId - show title
            if (collectionName === 'BlogComments' && key === 'blogId' && typeof value === 'object' && value !== null) {
                return <span style={{ fontSize: '12px' }}>{value.title || value._id || 'N/A'}</span>;
            }
            // Special handling for ProductPrices productId - show title
            if ((collectionName === 'ProductPrices' || collectionName === 'productprices') && key === 'productId' && typeof value === 'object' && value !== null) {
                return <span style={{ fontSize: '12px', fontWeight: 500 }}>{value.title || value._id || 'N/A'}</span>;
            }
            // Special handling for BlogComments userId - show email
            if (collectionName === 'BlogComments' && key === 'userId' && typeof value === 'object' && value !== null) {
                return <span style={{ fontSize: '12px' }}>{value.email || value._id || 'N/A'}</span>;
            }
            // Special handling for PurchaseOrders payment object (not array)
            if (collectionName === 'PurchaseOrders' && key === 'payment' && !Array.isArray(value) && value.method) {
                return (
                    <div style={{ maxWidth: '300px' }}>
                        <div style={{ 
                            padding: '8px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            fontSize: '11px'
                        }}>
                            <div style={{ fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                💳 Payment Details
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#6b7280' }}>
                                <div><strong>Method:</strong> {value.method}</div>
                                {value.status && <div><strong>Status:</strong> {value.status}</div>}
                                {value.transactionId && (
                                    <div style={{ fontFamily: 'monospace', fontSize: '10px' }}>
                                        <strong>Txn ID:</strong> {value.transactionId}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }
            
            // Generic payment display for other collections
            if (key === 'payment' && value.method && collectionName !== 'PurchaseOrders') {
                return <span style={{ fontSize: '12px' }}>💳 {value.method} - {value.status || 'N/A'}</span>;
            }
            if (key === 'shipping' && value.courierName) {
                const statusValue = value.status && Array.isArray(value.status) && value.status.length > 0 ? value.status[0] : 'N/A';
                return <span style={{ fontSize: '12px' }}>🚚 {value.courierName} - {statusValue}</span>;
            }
            // For other objects, show a compact preview
            const keys = Object.keys(value).slice(0, 3);
            const preview = keys.map(k => `${k}: ${typeof value[k] === 'object' ? '{...}' : String(value[k]).substring(0, 15)}`).join(', ');
            const more = Object.keys(value).length > 3 ? ` +${Object.keys(value).length - 3} more` : '';
            return (
                <span style={{ fontSize: '11px' }} title={JSON.stringify(value, null, 2)}>
                    {`{${preview}${more}}`}
                </span>
            );
        }
        if (typeof value === 'string') {
            return <span title={value} style={{ fontSize: '12px' }}>{value}</span>;
        }
        return <span style={{ fontSize: '12px' }}>{String(value)}</span>;
    };

    const allAvailableKeys = getAllKeys().filter(k => k !== '__v');
    const defaultVisibleKeys = getDefaultVisibleKeys(allAvailableKeys);
    const visibleKeys = allAvailableKeys.filter(k =>
        colOverrides[k] !== undefined ? colOverrides[k] : defaultVisibleKeys.includes(k)
    );

    // Load saved column widths
    useEffect(() => {
        if (collectionName) {
            const savedWidths = localStorage.getItem(`admin_col_width_${collectionName}`);
            if (savedWidths) {
                try {
                    setColumnWidths(JSON.parse(savedWidths));
                } catch (e) {
                    console.error("Error parsing saved column widths", e);
                }
            } else {
                setColumnWidths({});
            }
        }
    }, [collectionName]);

    // Load saved column visibility overrides
    useEffect(() => {
        if (collectionName) {
            const saved = localStorage.getItem(`admin_col_vis_${collectionName}`);
            if (saved) {
                try {
                    setColOverrides(JSON.parse(saved));
                } catch (e) {
                    setColOverrides({});
                }
            } else {
                setColOverrides({});
            }
        }
    }, [collectionName]);

    // Close settings panel when clicking outside
    useEffect(() => {
        if (!showColSettings) return;
        const handleClickOutside = (e) => {
            if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
                setShowColSettings(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColSettings]);

    // Handle resize start
    // eslint-disable-next-line no-unused-vars
    const handleResizeStart = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingCol(key);
        setStartX(e.clientX);
        setStartWidth(columnWidths[key] || 150); // Default width 150px
        
        // Add global event listeners
        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
    };

    // Handle resize move
    const handleResizeMove = (e) => {
        if (!resizingCol) return;
        requestAnimationFrame(() => {
            setColumnWidths(prev => {
                const diff = e.clientX - startX;
                const newWidth = Math.max(50, startWidth + diff); // Min width 50px
                return { ...prev, [resizingCol]: newWidth };
            });
        });
    };

    // Handle resize end
    const handleResizeEnd = () => {
        setResizingCol(null);
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        
        // Save to localStorage
        setColumnWidths(current => {
            localStorage.setItem(`admin_col_width_${collectionName}`, JSON.stringify(current));
            return current;
        });
    };
    
    // We need to use a ref to access the current state in the event listener since it's added to document
    // But since we are re-attaching listeners on every drag start, the closure should be fine? 
    // Actually, accessing state inside the event listener added in startHandler might be stale if we don't be careful.
    // A better way is to use a ref for the dragging state, OR simply rely on the fact that we're setting state based on 'startX' and 'startWidth' which are closed over.
    // However, 'resizingCol', 'startX', 'startWidth' state variables won't be updated in the 'handleResizeMove' closure if we define it outside.
    // Let's use a useEffect to manage the listeners to ensure they have fresh state, or use refs.
    // Using refs for mutable drag state is cleaner.
    
    const resizingState = React.useRef({ col: null, startX: 0, startWidth: 0 });

    const startResize = (e, key) => {
        e.preventDefault();
        e.stopPropagation(); // Stop sorting if header is clicked
        
        const currentWidth = columnWidths[key] || 200;
        resizingState.current = { col: key, startX: e.clientX, startWidth: currentWidth };
        
        document.addEventListener('mousemove', onResizeMove);
        document.addEventListener('mouseup', onResizeEnd);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    const onResizeMove = (e) => {
        if (!resizingState.current.col) return;
        
        const { col, startX, startWidth } = resizingState.current;
        const diff = e.clientX - startX;
        const newWidth = Math.max(80, startWidth + diff); // Min width 80
        
        setColumnWidths(prev => ({ ...prev, [col]: newWidth }));
    };

    const onResizeEnd = () => {
        if (resizingState.current.col) {
            localStorage.setItem(`admin_col_width_${collectionName}`, JSON.stringify(columnWidths));
        }
        
        resizingState.current = { col: null, startX: 0, startWidth: 0 };
        document.removeEventListener('mousemove', onResizeMove);
        document.removeEventListener('mouseup', onResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        // Persist final state
        setColumnWidths(prev => {
           localStorage.setItem(`admin_col_width_${collectionName}`, JSON.stringify(prev));
           return prev;
        });
    };

    return (
        <div className="admin-container w-full max-w-none px-0 pb-[20px]">
            <AdminTopNav activeTab="database" navPinned={navPinned} onTogglePin={() => setNavPinned(v => !v)} />
            <main className="admin-content">
                    <div className="p-[16px]" style={{ padding: 16, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <div className="admin-card-full-width rounded-[12px] border border-slider bg-bg p-[14px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div>
                                    <Link to="/admin/database" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '14px' }}>
                                        ← Back to Database
                                    </Link>
                                    <h1 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0", color: "#0f172a" }}>
                                        {collectionName} ({databaseName})
                                    </h1>
                                    <div style={{ fontSize: '14px', color: '#6b7280' }}>{count} total documents</div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {isAdmin && selectedRows.size > 0 && (
                                    <button
                                        onClick={handleDeleteSelected}
                                        className="small-button"
                                        style={{
                                            padding: '4px 12px',
                                            height: '28px',
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            minHeight: '28px !important',
                                            minWidth: 'auto !important'
                                        }}
                                    >
                                        Delete Selected ({selectedRows.size})
                                    </button>
                                    )}
                                    {isAdmin && (
                                    <button
                                        onClick={handleCreateNew}
                                        className="small-button"
                                        style={{
                                            padding: '4px 12px',
                                            height: '28px',
                                            backgroundColor: '#059669',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            minHeight: '28px !important',
                                            minWidth: 'auto !important'
                                        }}
                                    >
                                        Create New
                                    </button>
                                    )}
                                    <button
                                        onClick={fetchCollectionData}
                                        className="small-button"
                                        style={{
                                            padding: '4px 12px',
                                            height: '28px',
                                            backgroundColor: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            minHeight: '28px !important',
                                            minWidth: 'auto !important'
                                        }}
                                    >
                                        Refresh
                                    </button>
                                    <div style={{ position: 'relative' }} ref={colSettingsRef}>
                                        <button
                                            onClick={() => setShowColSettings(v => !v)}
                                            className="small-button"
                                            style={{
                                                padding: '0',
                                                height: '28px',
                                                width: '28px',
                                                backgroundColor: showColSettings ? '#eef2ff' : '#f3f4f6',
                                                color: showColSettings ? '#4f46e5' : '#374151',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="Column settings"
                                        >
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                                                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                                            </svg>
                                        </button>
                                        {showColSettings && (
                                            <div className="col-settings-panel">
                                                <div className="col-settings-header-row">
                                                    <span className="col-settings-title">Show in table</span>
                                                </div>
                                                <div className="col-settings-list">
                                                    {allAvailableKeys.map(key => {
                                                        const isVisible = colOverrides[key] !== undefined ? colOverrides[key] : defaultVisibleKeys.includes(key);
                                                        const fieldLabel = getFieldLabel(collectionName, key);
                                                        return (
                                                            <div key={key} className="col-settings-item">
                                                                <span className="col-settings-drag-handle">⋮⋮</span>
                                                                <span className="col-settings-col-name">{fieldLabel}</span>
                                                                <button
                                                                    className={`col-settings-eye-btn${isVisible ? ' active' : ''}`}
                                                                    onClick={() => toggleColumnVisibility(key)}
                                                                    title={isVisible ? 'Hide column' : 'Show column'}
                                                                >
                                                                    {isVisible ? (
                                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                                                        </svg>
                                                                    ) : (
                                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                                            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {loading && (
                                <div className="collection-skeleton-container">
                                    <table className="collection-skeleton-table">
                                        <thead>
                                            <tr>
                                                <th className="collection-skeleton-th" style={{ width: '40px' }}>
                                                    <div className="collection-skeleton-header-bar" style={{ width: '20px' }}></div>
                                                </th>
                                                <th className="collection-skeleton-th" style={{ width: '80px' }}>
                                                    <div className="collection-skeleton-header-bar" style={{ width: '50px' }}></div>
                                                </th>
                                                {visibleKeys.slice(0, 6).map((key, idx) => (
                                                    <th key={key || idx} className="collection-skeleton-th" style={{ width: '180px' }}>
                                                        <div className="collection-skeleton-header-bar" style={{ width: `${60 + (idx % 3) * 20}%` }}></div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...Array(6)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    <td className="collection-skeleton-td">
                                                        <div className="collection-skeleton-bar" style={{ width: '16px', height: '16px', borderRadius: '4px' }}></div>
                                                    </td>
                                                    <td className="collection-skeleton-td">
                                                        <div className="collection-skeleton-bar" style={{ width: '48px' }}></div>
                                                    </td>
                                                    {visibleKeys.slice(0, 6).map((key, colIdx) => (
                                                        <td key={key || colIdx} className="collection-skeleton-td">
                                                            <div 
                                                                className="collection-skeleton-bar" 
                                                                style={{ 
                                                                    width: `${Math.max(35, ((rowIdx * 19 + colIdx * 37) % 65) + 30)}%` 
                                                                }}
                                                            ></div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {error && <div style={{ color: "#b91c1c", marginBottom: 16 }}>{error}</div>}

                            {!loading && !error && data.length > 0 && (
                                <div style={{ 
                                    overflowX: 'auto', 
                                    overflowY: 'visible',
                                    width: '100%',
                                    maxWidth: '100%',
                                    WebkitOverflowScrolling: 'touch',
                                    position: 'relative',
                                    margin: '0 -16px',
                                    padding: '0 16px',
                                    boxSizing: 'border-box'
                                }}>
                                    <table style={{ 
                                        width: 'auto', 
                                        borderCollapse: 'collapse', 
                                        fontSize: '14px', 
                                        tableLayout: 'fixed',
                                        margin: 0
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                {isAdmin && (
                                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, width: '40px' }}>
                                                </th>
                                                )}
                                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, width: '80px' }}>Actions</th>
                                                {visibleKeys.map(key => {
                                                    const fieldLabel = getFieldLabel(collectionName, key);
                                                    const schema = getCollectionSchema(collectionName);
                                                    const isRequired = schema && schema.requiredFields.includes(key);
                                                    const width = columnWidths[key] || 200; // Default width
                                                    
                                                    return (
                                                        <th 
                                                            key={key} 
                                                            style={{ 
                                                                padding: '12px', 
                                                                textAlign: 'left', 
                                                                fontWeight: 600,
                                                                position: 'relative',
                                                                width: `${width}px`,
                                                                minWidth: `${width}px`,
                                                                maxWidth: `${width}px`,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                        >
                                                            <div
                                                                onClick={key === 'postNumber' && hasPostNumSort ? () => setPostNumSort(s => s === 'desc' ? 'asc' : 'desc') : undefined}
                                                                title={key === 'postNumber' && hasPostNumSort ? (postNumSort === 'desc' ? 'Descending (click for ascending)' : 'Ascending (click for descending)') : undefined}
                                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: key === 'postNumber' && hasPostNumSort ? 'pointer' : 'default', userSelect: 'none' }}
                                                            >
                                                                <span>
                                                                    {fieldLabel}
                                                                    {isRequired && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
                                                                </span>
                                                                {key === 'postNumber' && hasPostNumSort && (
                                                                    <span style={{ fontSize: '14px', lineHeight: 1, color: '#374151', flexShrink: 0 }}>
                                                                        {postNumSort === 'desc' ? '↓' : '↑'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div
                                                                onMouseDown={(e) => startResize(e, key)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    right: 0,
                                                                    top: 0,
                                                                    bottom: 0,
                                                                    width: '12px',
                                                                    cursor: 'col-resize',
                                                                    zIndex: 10,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                                title="Resize column"
                                                            >
                                                                <div style={{
                                                                    width: '1px',
                                                                    height: '70%',
                                                                    backgroundColor: '#9ca3af',
                                                                }} />
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayData.map((row, index) => {
                                                const rowId = String(row._id || row.id || index);
                                                return (
                                                <tr
                                                    key={row._id || row.id || index}
                                                    style={{
                                                        borderBottom: '1px solid #e5e7eb',
                                                        backgroundColor: selectedRows.has(rowId) ? '#eff6ff' : 'white'
                                                    }}
                                                >
                                                    <td style={{ padding: '12px', width: '40px' }}>
                                                        {isAdmin && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.has(rowId)}
                                                            onChange={() => handleSelectRow(rowId)}
                                                            className="admin-checkbox"
                                                            style={{ 
                                                                cursor: 'pointer',
                                                                width: '14px',
                                                                height: '14px',
                                                                minHeight: '14px !important',
                                                                minWidth: '14px !important'
                                                            }}
                                                        />
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', width: '80px' }}>
                                                        <Link
                                                            to={`/admin/database/${databaseType}/${collectionName}/${isAdmin ? 'edit' : 'view'}/${String(row._id || row.id || '')}`}
                                                            className="small-button"
                                                            style={{
                                                                padding: '4px 8px',
                                                                height: '24px',
                                                                backgroundColor: isAdmin ? '#2563eb' : '#6b7280',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                textDecoration: 'none',
                                                                display: 'inline-block',
                                                                lineHeight: '16px',
                                                                minHeight: '24px !important',
                                                                minWidth: 'auto !important'
                                                            }}
                                                        >
                                                            {isAdmin ? 'Edit' : 'View'}
                                                        </Link>
                                                    </td>
                                                    {visibleKeys.map(key => (
                                                        <td key={key} style={{
                                                            padding: '12px', 
                                                            verticalAlign: 'middle',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            borderBottom: '1px solid #e5e7eb'
                                                        }}>
                                                            {renderCellValue(key, row[key], String(row._id || row.id || ''))}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!loading && !error && data.length === 0 && (
                                <div style={{ color: "#6b7280", textAlign: 'center', padding: '40px' }}>
                                    No documents found in this collection.
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            <ConfirmDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={confirmDelete}
                message="Do you want to delete 1 or more selected data?"
                title="Confirm Deletion"
            />
        </div>
    );
}

